// src/pages/Financial/Quotes.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  CheckCircle as AcceptedIcon,
  Schedule as PendingIcon,
  Drafts as DraftIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';
import DevOnly from '../../components/DevOnly';

const QuotesManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Fetch quotes data
  const { data: quotesData, isLoading, error } = useQuery({
    queryKey: ['quotes', searchTerm, statusFilter, dateRange, paginationModel],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        params.append('skip', String(paginationModel.page * paginationModel.pageSize));
        params.append('limit', String(paginationModel.pageSize));
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (dateRange !== 'all') params.append('date_range', dateRange);
        
        const response = await apiClient.get(`/financial/quotes?${params.toString()}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching quotes:', error);
        return { quotes: [], total: 0, stats: {} };
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      // Handle both timestamp and string dates
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Try parsing as string date
        return dateString;
      }
      return date.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
      case 'signed':
        return 'success';
      case 'sent':
      case 'viewed':
        return 'info';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'default';
      case 'expired':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
      case 'signed':
        return <AcceptedIcon fontSize="small" />;
      case 'sent':
        return <SendIcon fontSize="small" />;
      case 'draft':
        return <DraftIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const statuses = ['all', 'draft', 'sent', 'viewed', 'accepted', 'expired'];
    setStatusFilter(statuses[newValue]);
  };

  const columns = [
    {
      field: 'quote_id',
      headerName: 'Quote #',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value ? `#${params.value}` : `#${params.row.id?.slice(-6)}`}
        </Typography>
      )
    },
    {
      field: 'quote_name',
      headerName: 'Quote Name',
      width: 200,
      renderCell: (params) => params.value || 'Untitled Quote'
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 200,
      renderCell: (params) => params.value || 'Unknown Client'
    },
    {
      field: 'issue_date',
      headerName: 'Issue Date',
      width: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'expiration_date',
      headerName: 'Expires',
      width: 120,
      renderCell: (params) => {
        const date = formatDate(params.value);
        const isExpired = params.value && new Date(params.value) < new Date();
        return (
          <Typography
            variant="body2"
            color={isExpired ? 'error' : 'textPrimary'}
          >
            {date}
          </Typography>
        );
      }
    },
    {
      field: 'quote_total',
      headerName: 'Total',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getStatusIcon(params.value)}
          <Chip
            label={params.value || 'Draft'}
            color={getStatusColor(params.value)}
            size="small"
          />
        </Box>
      )
    },
    {
      field: 'template_name',
      headerName: 'Template',
      width: 150,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => navigate(`/financial/quotes/${params.row.id}`)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.url && (
            <Tooltip title="Open Quote URL">
              <IconButton
                size="small"
                onClick={() => window.open(params.row.url, '_blank')}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.status === 'draft' && (
            <DevOnly>
              <>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/financial/quotes/${params.row.id}/edit`)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Send">
                  <IconButton
                    size="small"
                    onClick={() => console.log('Send quote:', params.row.id)}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            </DevOnly>
          )}
          {params.row.status !== 'draft' && (
            <Tooltip title="Email">
              <IconButton
                size="small"
                onClick={() => console.log('Email quote:', params.row.id)}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => console.log('Download quote:', params.row.id)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load quotes. Please try again later.
        </Alert>
      </Box>
    );
  }

  const stats = quotesData?.stats || {
    total_quotes: 0,
    total_amount: 0,
    accepted_quotes: 0,
    pending_quotes: 0,
    rejected_quotes: 0,
    expired: 0,
    totalValue: 0,
    acceptedValue: 0,
    conversionRate: 0
  };

  const quotes = quotesData?.quotes || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Quotes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage service quotes for clients
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/financial/quotes/create')}
            size="large"
          >
            Create Quote
          </Button>
        </DevOnly>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Quotes
              </Typography>
              <Typography variant="h5" component="div">
                {quotesData?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(stats.total_amount)} total value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Accepted
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {stats.accepted_quotes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accepted quotes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Conversion Rate
              </Typography>
              <Typography variant="h5" component="div">
                {stats.conversionRate?.toFixed(1)}%
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={stats.conversionRate || 0}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Pending
              </Typography>
              <Typography variant="h5" component="div" color="warning.main">
                {stats.pending_quotes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting response
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={`All (${quotesData?.total || 0})`} />
            <Tab label={`Draft (${stats.draft || 0})`} />
            <Tab label={`Sent (${stats.sent || 0})`} />
            <Tab label={`Viewed (${stats.viewed || 0})`} />
            <Tab label={`Accepted (${stats.accepted_quotes || 0})`} />
            <Tab label={`Expired (${stats.expired || 0})`} />
          </Tabs>
        </Box>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search by quote name, client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
          >
            More Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Quotes DataGrid */}
      <Paper>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={quotes}
            columns={columns}
            rowCount={quotesData?.total || 0}
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            checkboxSelection
            disableSelectionOnClick
            loading={isLoading}
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default QuotesManagement;